"use client";

import React, { useEffect, useRef, useState } from "react";
import { features, featureTrustItems } from "@/lib/data";
import Image from "next/image";
import FeaturesCard from "./features-card";

const WhatsIncluded = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    // On mobile the section is very tall so threshold:0.2 fires too late —
    // the heading has already scrolled well out of view before animating.
    // Use a near-zero threshold on mobile so it fires as soon as the section appears.
    const isMobile = window.innerWidth < 768;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      isMobile
        ? { threshold: 0, rootMargin: "0px 0px -8% 0px" }
        : { threshold: 0.2 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);


  // bg-[#f8fafc] this is what's added
  return (
    <section
      ref={sectionRef}
      id="services"
      className="px-4 sm:px-6.5 lg:px-10 xl:px-[57.5px] 2xl:container 2xl:mx-auto 2xl:px-[57.5px] bg-[#f8fafc]  text-sm md:text-xs lg:text-sm xl:text-base"
    >
      {/* trigger it here */}
      <div className="space-y-5 lg:space-y-8 xl:space-y-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between text-surface text-sm md:text-xs lg:text-sm xl:text-base space-y-5 md:space-y-0">
          <div className="space-y-4.5 md:space-y-2.5 xl:space-y-4 max-w-95 lg:max-w-107 xl:max-w-133.5">
            <p className="inline-flex gap-x-2 items-center text-primary bg-surface-white py-1.5 px-2.5 xl:px-4 uppercase rounded-[50px] font-medium outline outline-surface-light/70 shadow-[0px_0px_25px_0px_#0000001A]">
              <span
                className={`h-0.5 bg-primary transition-all duration-700 ease-out delay-150 ${isVisible ? "w-4 xl:w-6" : "w-0 xl:w-0"}`}
              />
              <span>what&apos;s included</span>
            </p>
            <div className="overflow-hidden pb-2">
              <h2
                className={`text-[28px] lg:text-[37px] xl:text-5xl font-semibold leading-tight tracking-normal transition-transform duration-[1500ms] delay-[400ms] ease-[cubic-bezier(0.175,0.885,0.32,1.275)] ${isVisible ? "translate-y-0" : "translate-y-[120%]"}`}
              >
                Everything You Need, All in{" "}
                <span className="text-primary font-normal">
                  One Experience
                </span>{" "}
              </h2>
            </div>
          </div>

          <div className="flex flex-col gap-y-3.5 md:gap-y-2.5 lg:gap-y-4 xl:gap-y-6 md:max-w-68 lg:max-w-75 xl:max-w-90.5">
            <p className="order-2 md:order-1 leading-5.5 md:leading-4 xl:leading-5">
              From your first trimester to postpartum recovery, every feature is
              designed for the modern mother who wants structured, trusted
              support.
            </p>
            <div className="order-1 md:order-2 bg-primary/10 outline-[0.5px] outline-surface-light rounded-[20px] py-3 px-3.5 md:p-2 lg:p-2.5 xl:p-3 space-x-2.5 xl:space-x-3 flex items-center drop-shadow-[0_0_25px_rgba(226,226,226,0.7)]">
              <span className="shrink-0 font-bold text-primary text-[28px] lg:text-[37px] xl:text-5xl ">
                15
              </span>
              <div className=" lg:space-y-1 xl:space-y-2">
                <p className="font-medium text-surface-black text-lg lg:text-xl xl:text-2xl leading-none">
                  Core Features
                </p>
                <span className="leading-4.5 xl:leading-5.5">
                  across 3 categories
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 sm:gap-4 lg:gap-6  xl:gap-8">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="relative pr-2 lg:pr-2.5 pl-4 lg:pl-6 pb-7 lg:pb-10 pt-5 xl:pt-8 bg-surface-white outline-[1px] outline-surface-light/70 rounded-[30px] overflow-hidden"
            >
              <div className="space-y-4 lg:space-y-8 xl:space-y-12">
                <div className="space-y-2 lg:space-y-2.5 xl:space-y-4 pb-3 lg:pb-4.5 xl:pb-6 border-b-[0.5px] border-surface/50 ">
                  <div className="space-y-1.5 lg:space-y-2 xl:space-y-3 text-primary leading-5.5 font-medium ">
                    <div className="relative size-12 lg:size-14">
                      <Image
                        src={`/images/what's-included/${feature.icon}`}
                        alt={feature.title}
                        fill
                        className="size-full object-contain object-center"
                      />
                    </div>

                    <p>{feature.category}</p>
                  </div>

                  <div className="space-y-1.5 lg:space-y-2 xl:space-y-3">
                    <h3 className="font-semibold text-xl xl:text-2xl leading-5.5">
                      {feature.title}
                    </h3>
                    <span>{feature.description}</span>
                  </div>
                </div>

                <ul className="space-y-2.5 lg:space-y-4">
                  {feature.items.map((item) => (
                    <li
                      className="flex items-center justify-start gap-x-2 xl:gap-x-3  pb-2.5 lg:pb-3 border-b-[0.5px] border-surface/50"
                      key={item.text}
                    >
                      <span
                        className={`shrink-0 bg-primary/10 rounded-[5px] p-0.75 ${feature.id === 1 ? "bg-primary/10 text-primary" : feature.id === 2 ? "bg-[#00A76F]/10 text-[#00A76F]" : "bg-[#8777D9]/10 text-[#8777D9]"}`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 lg:w-5 xl:w-6"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      </span>

                      <p className="leading-5.5 text-surface">
                        {item.prefix && (
                          <span
                            className={`${feature.id === 1 ? "text-primary" : feature.id === 2 ? "text-[#00A76F]" : "text-[#8777D9]"}`}
                          >
                            {item.prefix}
                          </span>
                        )}
                        {item.text}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-2 lg:mt-3 border-t-2 border-dashed border-surface/30 pt-4">
                <p className="inline-flex gap-x-2 lg:gap-x-3">
                  <span
                    className={`text-xl lg:text-[26px] xl:text-[32px] font-bold leading-4.5 lg:leading-5.5 ${feature.id === 1 ? "text-primary" : feature.id === 2 ? "text-[#00A76F]" : "text-[#8777D9]"}`}
                  >
                    {feature.stat}
                  </span>
                  <span className="text-surface-black leading-5.5">
                    {feature.statLabel}
                  </span>
                </p>
              </div>

              <div
                className={`size-18.5 xl:size-22.5 rounded-full absolute -top-5.5 xl:-top-6.25 -right-1.5  ${feature.id === 1 ? "bg-primary/10" : feature.id === 2 ? "bg-[#F0F8F5]" : "bg-[#F6F5FC]"}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="py-9 sm:py-8 md:py-6 xl:py-8">
        <FeaturesCard details={featureTrustItems} />
      </div>
    </section>
  );
};

export default WhatsIncluded;
